// Wrapper around window.Telegram.WebApp + a dev fallback.

interface SafeAreaInset {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

type TgWebApp = {
  ready: () => void;
  expand: () => void;
  /** Telegram-WebApp version string (e.g. "6.0", "7.4"). */
  version?: string;
  isVersionAtLeast?: (version: string) => boolean;
  initData: string;
  /** Bot API 8.0+: system safe area (notch / status bar). */
  safeAreaInset?: SafeAreaInset;
  /** Bot API 8.0+: extra inset for Telegram's own chrome (top header,
   *  drag handle, close button). Stacks on top of safeAreaInset. */
  contentSafeAreaInset?: SafeAreaInset;
  initDataUnsafe?: {
    user?: {
      id: number;
      username?: string;
      first_name?: string;
      last_name?: string;
      language_code?: string;
    };
  };
  colorScheme: "light" | "dark";
  HapticFeedback?: {
    impactOccurred: (style: "light" | "medium" | "heavy" | "rigid" | "soft") => void;
    notificationOccurred: (type: "error" | "success" | "warning") => void;
    selectionChanged: () => void;
  };
  MainButton?: {
    text: string;
    color?: string;
    textColor?: string;
    isVisible: boolean;
    isActive: boolean;
    isProgressVisible: boolean;
    setText: (t: string) => any;
    onClick: (cb: () => void) => any;
    offClick: (cb: () => void) => any;
    show: () => any;
    hide: () => any;
    enable: () => any;
    disable: () => any;
    showProgress: (leaveActive?: boolean) => any;
    hideProgress: () => any;
    setParams: (p: {
      text?: string;
      color?: string;
      text_color?: string;
      is_active?: boolean;
      is_visible?: boolean;
    }) => any;
  };
  setHeaderColor?: (c: string) => void;
  setBackgroundColor?: (c: string) => void;
  themeParams?: Record<string, string>;
  openTelegramLink?: (url: string) => void;
  openInvoice?: (
    url: string,
    callback?: (status: "paid" | "cancelled" | "failed" | "pending") => void,
  ) => void;
  showAlert?: (msg: string) => void;
  // Bot API 8.0+, hides the entire chrome including the `@bot_username`
  // attribution footer that Telegram shows under menu-button mini apps.
  // Requires a user gesture on some platforms; safe to ignore failures.
  requestFullscreen?: () => void;
  exitFullscreen?: () => void;
  isFullscreen?: boolean;
  onEvent?: (event: string, cb: (...args: unknown[]) => void) => void;
  offEvent?: (event: string, cb: (...args: unknown[]) => void) => void;
  disableVerticalSwipes?: () => void;
  /** "ios" | "android" | "tdesktop" | "macos" | "weba" | "unknown" */
  platform?: string;
};

declare global {
  interface Window {
    Telegram?: { WebApp?: TgWebApp };
  }
}

export function tg(): TgWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

/**
 * Single bumpable minimum-version constant. Used to gate everything that
 * 6.0 clients don't support: HapticFeedback, setHeaderColor,
 * setBackgroundColor. Calling any of these on a 6.0 client prints a noisy
 * console warning per invocation.
 */
export const TG_MIN_VERSION = "6.1";

/** True if the loaded Telegram WebApp client is at least `version`. */
export function supportsTgFeature(version: string): boolean {
  const app = tg();
  if (!app) return false;
  return typeof app.isVersionAtLeast === "function" && app.isVersionAtLeast(version);
}

/**
 * Push Telegram's safe-area insets into CSS variables on <html> so the
 * layout can reserve space for the iOS notch + Telegram's own header.
 *
 * env(safe-area-inset-top) is unreliable inside Telegram Mini Apps on iOS,
 * see TelegramMessenger/Telegram-iOS#1377. The Bot API 8.0+ insets are
 * the only thing we can trust. Stacks: `safeAreaInset` covers the system
 * status bar / notch; `contentSafeAreaInset` is the extra room above
 * which Telegram's own chrome (header buttons, drag handle) renders. Our
 * components add both when they need to clear the top edge.
 */
function applySafeAreaVars(app: TgWebApp): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const sa = app.safeAreaInset;
  const csa = app.contentSafeAreaInset;
  const setVar = (name: string, n: number | undefined) => {
    if (typeof n === "number" && Number.isFinite(n)) {
      root.style.setProperty(name, `${Math.max(0, Math.round(n))}px`);
    }
  };
  setVar("--tg-safe-top", sa?.top);
  setVar("--tg-safe-bottom", sa?.bottom);
  setVar("--tg-safe-left", sa?.left);
  setVar("--tg-safe-right", sa?.right);
  setVar("--tg-content-safe-top", csa?.top);
  setVar("--tg-content-safe-bottom", csa?.bottom);
  setVar("--tg-content-safe-left", csa?.left);
  setVar("--tg-content-safe-right", csa?.right);
}

export function initTelegram(): void {
  const app = tg();
  if (!app) return;
  try {
    app.ready();
    app.expand();
    // Initial inset snapshot. Both events fire again after expand /
    // fullscreen change, see the listeners below.
    applySafeAreaVars(app);
    if (typeof app.onEvent === "function") {
      const refresh = () => applySafeAreaVars(app);
      app.onEvent("safe_area_changed", refresh);
      app.onEvent("content_safe_area_changed", refresh);
      app.onEvent("viewportChanged", refresh);
    }
    if (supportsTgFeature(TG_MIN_VERSION)) {
      app.setHeaderColor?.("#fbfaf6");
      app.setBackgroundColor?.("#fbfaf6");
    }
    // Fullscreen mode (Bot API 8.0+) hides the bot-username footer that
    // Telegram renders under menu-button mini apps. ONLY trigger on real
    // mobile clients, on Telegram Desktop / macOS / Web fullscreen blows
    // up the centred phone-card layout (the 420px frame stretches to fill
    // the entire window, which is unreadable). Desktop users keep the
    // standard floating Mini App window.
    const platform = (app.platform ?? "unknown").toLowerCase();
    const isMobile = platform === "ios" || platform === "android";
    if (
      isMobile &&
      supportsTgFeature("8.0") &&
      typeof app.requestFullscreen === "function"
    ) {
      try {
        app.requestFullscreen();
      } catch {
        /* user-gesture requirement; retried on first tap */
        if (typeof window !== "undefined") {
          const retry = () => {
            try {
              app.requestFullscreen?.();
            } catch {
              /* ignore */
            }
            window.removeEventListener("pointerdown", retry);
            window.removeEventListener("touchend", retry);
          };
          window.addEventListener("pointerdown", retry, { once: true, passive: true });
          window.addEventListener("touchend", retry, { once: true, passive: true });
        }
      }
    }
  } catch {
    /* ignore */
  }
}

export function getInitData(): string {
  return tg()?.initData || "";
}

/**
 * Build a Telegram share URL for an idea and open it via the WebApp helper
 * when running inside Telegram, or a fresh tab otherwise. Centralised here
 * so callers (IdeaModal modal + SavedTab card row) don't drift apart.
 */
export function shareIdea(title: string, subtitle: string | undefined): void {
  const text = subtitle ? `${title}. ${subtitle}` : title;
  const url =
    `https://t.me/share/url` +
    `?url=${encodeURIComponent(window.location.href)}` +
    `&text=${encodeURIComponent(text)}`;
  const app = tg();
  if (app?.openTelegramLink) {
    app.openTelegramLink(url);
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

export function getDevUserHeader(): Record<string, string> {
  if (import.meta.env.PROD) return {};
  if (tg()?.initData) return {};
  return {
    "x-dev-tg-user": JSON.stringify({
      id: 100000001,
      username: "dev",
      first_name: "Dev",
      language_code: navigator.language?.startsWith("ru") ? "ru" : "en",
    }),
  };
}
